import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BottomSheet } from "@core/components";
import { Icon } from "@core/assets";
import { formatBytes } from "@core/utils";
import { toLocale } from "@core/locale";
import { DocumentsButton } from "./DocumentsButton";
import type { DocumentMutationResult } from "../hooks";
import type { ApplicationDocumentRow, DocumentRequirement } from "../types";

type FileRejectionReason = "extension" | "mime" | "size";

/** Client-side pre-check only — the server re-validates all three independently and is the real gate. */
function rejectionReason(
  file: File,
  requirement: DocumentRequirement
): FileRejectionReason | null {
  const dot = file.name.lastIndexOf(".");
  const extension = dot === -1 ? "" : file.name.slice(dot + 1).toLowerCase();
  if (!extension || !requirement.accepted_extensions.includes(extension))
    return "extension";
  if (
    requirement.accepted_mime_types.length > 0 &&
    !requirement.accepted_mime_types.includes(file.type)
  ) {
    return "mime";
  }
  if (file.size <= 0 || file.size > requirement.max_size_bytes) return "size";
  return null;
}

const STATUS_ICON = {
  AVAILABLE: "circle-check",
  PENDING: "clock",
  REJECTED: "alert",
} as const;

const STATUS_LABEL_KEYS = {
  AVAILABLE: "dossier.documents.status.available",
  PENDING: "dossier.documents.status.pending",
  REJECTED: "dossier.documents.status.rejected",
} as const;

/** One already-attached file — « Ligne document », attached state. */
function AttachedDocumentLine({
  document,
  isDisabled,
  onDelete,
}: {
  document: ApplicationDocumentRow;
  isDisabled: boolean;
  onDelete: (documentId: string) => Promise<DocumentMutationResult>;
}) {
  const { t, i18n } = useTranslation("acquire");
  const locale = toLocale(i18n.resolvedLanguage ?? "fr");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusKey =
    document.status === "AVAILABLE" ||
    document.status === "PENDING" ||
    document.status === "REJECTED"
      ? document.status
      : "PENDING";

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    const result = await onDelete(document.id);
    if (!result.ok) {
      setIsDeleting(false);
      setError(result.message);
    }
    // On success the row disappears on the next render (the sheet re-reads `application.documents`
    // wholesale after the refetch) — only the failure path needs to reset the spinner.
  };

  return (
    <div className="flex flex-col gap-xxs py-sm">
      <div className="flex min-h-16 items-start gap-sm">
        <span
          aria-hidden
          className={
            "shrink-0 " +
            (statusKey === "AVAILABLE"
              ? "text-(--feedback-success-icon)"
              : statusKey === "REJECTED"
                ? "text-(--feedback-warning-icon)"
                : "text-(--text-tertiary)")
          }
        >
          <Icon name={STATUS_ICON[statusKey]} size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="wrap-break-word text-sm-medium text-(--text-primary)">
            {document.filename}
          </p>
          <p className="text-xs-regular text-(--text-tertiary)">
            {formatBytes(document.size_bytes, locale)} ·{" "}
            {t(STATUS_LABEL_KEYS[statusKey])}
          </p>
        </div>
        <button
          type="button"
          disabled={isDisabled || isDeleting}
          onClick={() => void handleDelete()}
          aria-label={t("dossier.documents.remove-label", {
            filename: document.filename,
          })}
          className="shrink-0 rounded-sm p-1 text-(--text-tertiary) outline-none hover:text-(--text-error) focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon
            name={isDeleting ? "loading" : "trash"}
            size={18}
            className={isDeleting ? "animate-spin" : undefined}
          />
        </button>
      </div>
      {error ? (
        <p className="text-xs-regular text-(--text-error)">{error}</p>
      ) : null}
    </div>
  );
}

/**
 * One requirement — « Ligne document » (Figma `432:19014`): an empty-state row with an "Ajouter"
 * link, or one line per already-attached file, plus another "Ajouter" affordance underneath while
 * still under `max_files`. Kept as one component (not split further) because the empty/attached
 * states share the same status-icon/text/action layout Figma draws as a single row shape.
 */
function RequirementLine({
  requirement,
  documents,
  isDisabled,
  onUpload,
  onDelete,
}: {
  requirement: DocumentRequirement;
  documents: ApplicationDocumentRow[];
  isDisabled: boolean;
  onUpload: (
    requirementId: string,
    file: File
  ) => Promise<DocumentMutationResult>;
  onDelete: (documentId: string) => Promise<DocumentMutationResult>;
}) {
  const { t, i18n } = useTranslation("acquire");
  const locale = toLocale(i18n.resolvedLanguage ?? "fr");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const atLimit = documents.length >= requirement.max_files;

  const handleFilePicked = async (file: File) => {
    setError(null);
    const rejection = rejectionReason(file, requirement);
    if (rejection) {
      setError(t(`dossier.documents.reject.${rejection}`));
      return;
    }
    setIsUploading(true);
    const result = await onUpload(requirement.id, file);
    setIsUploading(false);
    if (!result.ok) setError(result.message);
  };

  return (
    <div>
      {documents.map((document) => (
        <AttachedDocumentLine
          key={document.id}
          document={document}
          isDisabled={isDisabled}
          onDelete={onDelete}
        />
      ))}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={requirement.accepted_extensions
          .map((extension) => `.${extension}`)
          .join(",")}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void handleFilePicked(file);
        }}
      />

      {atLimit ? null : (
        <button
          type="button"
          disabled={isDisabled || isUploading}
          onClick={() => inputRef.current?.click()}
          className="flex min-h-16 w-full items-start gap-sm py-sm text-start outline-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          <span aria-hidden className="shrink-0 text-(--text-tertiary)">
            <Icon
              name={isUploading ? "loading" : "circle-dashed"}
              size={20}
              className={isUploading ? "animate-spin" : undefined}
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm-medium text-(--text-primary)">
              {requirement.name}
              {requirement.is_required ? null : (
                <span className="ms-1.5 text-xs-regular text-(--text-tertiary)">
                  {t("dossier.documents.optional")}
                </span>
              )}
            </p>
            <p className="text-xs-regular text-(--text-tertiary)">
              {requirement.accepted_extensions
                .map((extension) => extension.toUpperCase())
                .join(" · ")}
              {" · "}
              {t("dossier.documents.max-size", {
                size: formatBytes(requirement.max_size_bytes, locale),
              })}
            </p>
          </div>
          <span className="shrink-0 text-sm-semibold text-(--text-link) underline">
            {t("dossier.documents.add")}
          </span>
        </button>
      )}
      {error ? (
        <p className="pb-sm text-xs-regular text-(--text-error)">{error}</p>
      ) : null}
    </div>
  );
}

/**
 * « Documents » — runs ALONGSIDE the four steps, not as one of them (US-E3-08 §1: "available from
 * the moment the dossier opens, alongside the questions rather than after them").
 *
 * ## A sheet, not an inline panel
 *
 * Figma's own name for this is "feuille" (sheet, `432:19080`) — a bottom sheet opened from a small
 * ring+badge button in the persistent action bar (`DocumentsButton`), not content sitting inline in
 * the page flow. `DossierFoundationScreen`/`ReviewScreen` render this ONE component in their bottom
 * bar; it owns its own open/closed state, so neither caller needs to know a sheet exists.
 *
 * Required requirements render under "OBLIGATOIRES" first, optional ones under "FACULTATIFS" —
 * same grouping Figma draws, and the same reason a required document blocks submit while an
 * optional one only says so next to its own row.
 *
 * ## What's here, and what deliberately isn't
 *
 * A real presigned upload against the round's actual requirements (`schema.documents.requirements`)
 * — three genuine calls (intent → direct-to-storage POST → complete), server-verified on landing.
 * What's NOT here, by explicit choice made ahead of building this: on-device compression, resumable
 * upload surviving a backgrounded browser, multi-page photo assembly. Those are the D14 spike this
 * story is blocked on — building them without the spike having run would be guessing at acceptance
 * thresholds the spike is supposed to measure. So a large phone photo over a slow connection is
 * exactly as slow as the file is; there is no reduction step.
 */
export function DocumentsPanel({
  requirements,
  documents,
  isDisabled,
  onUpload,
  onDelete,
}: {
  requirements: DocumentRequirement[];
  documents: ApplicationDocumentRow[];
  isDisabled: boolean;
  onUpload: (
    requirementId: string,
    file: File
  ) => Promise<DocumentMutationResult>;
  onDelete: (documentId: string) => Promise<DocumentMutationResult>;
}) {
  const { t } = useTranslation("acquire");
  const [isOpen, setIsOpen] = useState(false);

  if (requirements.length === 0) return null;

  const required = requirements.filter(
    (requirement) => requirement.is_required
  );
  const optional = requirements.filter(
    (requirement) => !requirement.is_required
  );
  const sentCount = required.filter((requirement) =>
    documents.some(
      (document) =>
        document.requirement_id === requirement.id &&
        document.status === "AVAILABLE"
    )
  ).length;

  return (
    <>
      <DocumentsButton
        requirements={requirements}
        documents={documents}
        onPress={() => setIsOpen(true)}
      />
      <BottomSheet
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        title={t("dossier.documents.title")}
        description={t("dossier.documents.subtitle", {
          sent: sentCount,
          total: required.length,
        })}
        footer={
          <p className="text-xs-regular text-(--text-tertiary)">
            {t("dossier.documents.offline-note")}
          </p>
        }
      >
        <div className="flex flex-col gap-lg pb-lg">
          {required.length > 0 ? (
            <div className="flex flex-col">
              <p className="pb-xs text-xs-semibold text-(--text-tertiary)">
                {t("dossier.documents.required-label")}
              </p>
              <div className="flex flex-col divide-y divide-(--border-default)">
                {required.map((requirement) => (
                  <RequirementLine
                    key={requirement.id}
                    requirement={requirement}
                    documents={documents.filter(
                      (document) => document.requirement_id === requirement.id
                    )}
                    isDisabled={isDisabled}
                    onUpload={onUpload}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {optional.length > 0 ? (
            <div className="flex flex-col">
              <p className="pb-xs text-xs-semibold text-(--text-tertiary)">
                {t("dossier.documents.optional")}
              </p>
              <div className="flex flex-col divide-y divide-(--border-default)">
                {optional.map((requirement) => (
                  <RequirementLine
                    key={requirement.id}
                    requirement={requirement}
                    documents={documents.filter(
                      (document) => document.requirement_id === requirement.id
                    )}
                    isDisabled={isDisabled}
                    onUpload={onUpload}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
}
